// `/api/v1/remediation-policy`: the workspace's remediation policy (#260, C3).
//
// Read off `control_plane/routes/policy.py`. `GET` answers the current revision
// of every `(resource type, root property)` the workspace has decided about and the
// modes this surface may write; `POST` writes one property one revision on,
// conditional on the revision the writer read, under a step-up grant minted for
// this purpose.
//
// **The modes come from the server.** `modes` is `WRITABLE_MODES` in the
// eligibility module, and the form offers that list and nothing else, so the
// one list lives in one place. A stored row can still carry a mode the form will
// not offer (`automatic`, which the seam refuses); it is shown as it is.
//
// **A revision is two calls.** `POST /auth/step-up` with this purpose mints the
// grant, and the grant rides the revision's body as `step_up_grant`. A grant for
// any other purpose does not open the route.

import type { Api } from "./http";

export const POLICY_PATH = "/api/v1/remediation-policy";
export const STEP_UP_PATH = "/auth/step-up";
/** The purpose `routes/policy.py::STEP_UP_PURPOSE` asks the grant to name. */
export const STEP_UP_PURPOSE = "remediation_policy";

/** One narrowing: these accounts AND these identifiers. Empty on an axis is any. */
export interface PolicyScope {
  account_ids: string[];
  identifier_patterns: string[];
}

/** The current revision for one `(resource type, root property)`. */
export interface PolicyRow {
  resource_type: string;
  root_property: string;
  mode: string;
  /** None means any account and any identifier; several match when ANY does. */
  scopes: PolicyScope[];
  revision: number;
  revised_at: string;
}

export interface PolicyRead {
  policies: PolicyRow[];
  /** The modes a revision may name. */
  modes: string[];
  /**
   * How many of the workspace's accounts the platform applies an approved change in
   * (`store/changes.py::APPLYING_ACCOUNT`, #910). With none, every change is applied by hand.
   */
  applying_accounts: number;
}

/** One property, one revision on from `expected_revision` (0 for "there was none"). */
export interface PolicyRevision {
  resource_type: string;
  root_property: string;
  mode: string;
  scopes: PolicyScope[];
  expected_revision: number;
}

/** What the step-up is proved with: a method and its credential. */
export interface StepUp {
  method: string;
  credential: string;
}

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

function asScope(value: unknown): PolicyScope | null {
  if (typeof value !== "object" || value === null) return null;
  const scope = value as Record<string, unknown>;
  return {
    account_ids: strings(scope.account_ids),
    identifier_patterns: strings(scope.identifier_patterns),
  };
}

export function asPolicyRow(value: unknown): PolicyRow | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.resource_type !== "string" || typeof row.root_property !== "string") return null;
  return {
    resource_type: row.resource_type,
    root_property: row.root_property,
    mode: typeof row.mode === "string" ? row.mode : "",
    scopes: Array.isArray(row.scopes)
      ? row.scopes.map(asScope).filter((s): s is PolicyScope => s !== null)
      : [],
    revision: typeof row.revision === "number" ? row.revision : 0,
    revised_at: typeof row.revised_at === "string" ? row.revised_at : "",
  };
}

export function policyFrom(body: unknown): PolicyRead {
  if (typeof body !== "object" || body === null) {
    return { policies: [], modes: [], applying_accounts: 0 };
  }
  const read = body as Record<string, unknown>;
  const policies = Array.isArray(read.policies)
    ? read.policies.map(asPolicyRow).filter((r): r is PolicyRow => r !== null)
    : [];
  return {
    policies,
    modes: strings(read.modes),
    // Unread is none: the screen never claims an account applies changes it was not told of.
    applying_accounts: typeof read.applying_accounts === "number" ? read.applying_accounts : 0,
  };
}

export const readPolicy = async (api: Api): Promise<PolicyRead> =>
  policyFrom(await api.get<unknown>(POLICY_PATH));

/** The revision `expected_revision` names for a property: its current one, or 0. */
export function currentRevision(
  policies: PolicyRow[],
  resourceType: string,
  rootProperty: string,
): number {
  const row = policies.find(
    (p) => p.resource_type === resourceType && p.root_property === rootProperty,
  );
  return row === undefined ? 0 : row.revision;
}

/** Step up for this purpose, then revise; the grant from the first call rides the second. */
export async function revisePolicy(
  api: Api,
  revision: PolicyRevision,
  stepUp: StepUp,
): Promise<PolicyRow> {
  if (stepUp.credential === "") {
    throw new Error("A policy revision needs you to confirm it is you.");
  }
  const granted = await api.post<{ grant?: unknown }>(STEP_UP_PATH, {
    method: stepUp.method,
    credential: stepUp.credential,
    purpose: STEP_UP_PURPOSE,
  });
  const grant = typeof granted?.grant === "string" ? granted.grant : "";
  const row = asPolicyRow(await api.post<unknown>(POLICY_PATH, { ...revision, step_up_grant: grant }));
  if (row === null) throw new Error("The policy route answered with a row this console cannot read.");
  return row;
}
